import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';
import { DEFAULT_VIEW, type ViewState } from './mapDisplay';
import { zoomAround, type ScreenPoint } from './geometry';

export interface MapViewport {
  /** Committed view used for rendering. */
  view: ViewState;
  /**
   * Apply a view change. Gestures batch into one render per animation frame;
   * `immediate` (buttons, keys, resets) renders synchronously.
   */
  update: (
    next: (view: ViewState) => ViewState,
    options?: { immediate?: boolean }
  ) => void;
  reset: () => void;
}

/**
 * Pan/zoom state of one pane. The latest value lives in a ref so pointer
 * and wheel gestures can accumulate between frames; React state is
 * committed at most once per animation frame.
 */
export const useMapViewport = (): MapViewport => {
  const [view, setView] = useState<ViewState>(DEFAULT_VIEW);
  const latest = useRef<ViewState>(DEFAULT_VIEW);
  const frame = useRef<number | null>(null);

  const flush = useCallback(() => {
    frame.current = null;
    setView(latest.current);
  }, []);

  const update = useCallback<MapViewport['update']>(
    (next, { immediate = false } = {}) => {
      latest.current = next(latest.current);
      if (immediate) {
        if (frame.current !== null) {
          cancelAnimationFrame(frame.current);
          frame.current = null;
        }
        setView(latest.current);
      } else if (frame.current === null) {
        frame.current = requestAnimationFrame(flush);
      }
    },
    [flush]
  );

  const reset = useCallback(
    () => update(() => DEFAULT_VIEW, { immediate: true }),
    [update]
  );

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    []
  );

  return useMemo(() => ({ view, update, reset }), [view, update, reset]);
};

/** Wheel delta in pixels regardless of the device's delta mode. */
const wheelPixels = (event: WheelEvent, pageHeight: number): number => {
  const unit =
    event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? pageHeight : 1;
  return Math.max(-240, Math.min(240, event.deltaY * unit));
};

/** Zoom factor for one wheel event; trackpad pinches arrive with ctrlKey. */
export const wheelZoomFactor = (event: WheelEvent, pageHeight = 800): number =>
  Math.exp(-wheelPixels(event, pageHeight) * (event.ctrlKey ? 0.01 : 0.0018));

/**
 * Attach a non-passive wheel listener so wheel zoom can prevent the page
 * from scrolling (React's onWheel is passive and cannot).
 */
export const useNonPassiveWheel = (
  ref: RefObject<Element>,
  onWheel: (event: WheelEvent) => void
) => {
  const handler = useRef(onWheel);
  useLayoutEffect(() => {
    handler.current = onWheel;
  });
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const listener = (event: Event) => handler.current(event as WheelEvent);
    element.addEventListener('wheel', listener, { passive: false });
    return () => element.removeEventListener('wheel', listener);
  }, [ref]);
};

const DRAG_THRESHOLD = 3;

interface Gesture {
  pointers: Map<number, ScreenPoint>;
  travel: number;
  pinch: { distance: number; mid: ScreenPoint } | null;
}

/**
 * Pointer and wheel gestures for one pane: drag to pan, wheel or pinch to
 * zoom about the pointer, double-click to zoom in. Pointer downs on map
 * points are left to the points. A click that ends a drag is swallowed so
 * panning does not clear the selection.
 */
export const usePanZoom = (
  svgRef: RefObject<SVGSVGElement>,
  viewport: MapViewport,
  origin: ScreenPoint
) => {
  const [panning, setPanning] = useState(false);
  const gesture = useRef<Gesture | null>(null);
  const suppressClick = useRef(false);
  const { update } = viewport;

  const local = (event: { clientX: number; clientY: number }): ScreenPoint => {
    const rect = svgRef.current?.getBoundingClientRect();
    return {
      x: event.clientX - (rect?.left ?? 0),
      y: event.clientY - (rect?.top ?? 0),
    };
  };

  useNonPassiveWheel(svgRef, (event) => {
    event.preventDefault();
    const factor = wheelZoomFactor(event, window.innerHeight);
    if (!Number.isFinite(factor) || factor === 1) return;
    const pointer = local(event);
    const anchor =
      Number.isFinite(pointer.x) && Number.isFinite(pointer.y)
        ? pointer
        : origin;
    update((view) => zoomAround(view, factor, anchor, origin));
  });

  const pinchState = (pointers: Map<number, ScreenPoint>) => {
    const [a, b] = [...pointers.values()];
    return {
      distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
      mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    };
  };

  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if ((event.target as Element).closest('[data-map-point]')) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const current = gesture.current ?? {
      pointers: new Map(),
      travel: 0,
      pinch: null,
    };
    current.pointers.set(event.pointerId, local(event));
    current.pinch =
      current.pointers.size === 2 ? pinchState(current.pointers) : null;
    gesture.current = current;
    setPanning(true);
  };

  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    const current = gesture.current;
    const previous = current?.pointers.get(event.pointerId);
    if (!current || !previous) return;
    const point = local(event);
    current.pointers.set(event.pointerId, point);
    if (current.pointers.size === 1) {
      const dx = point.x - previous.x;
      const dy = point.y - previous.y;
      current.travel += Math.hypot(dx, dy);
      update((view) => ({
        ...view,
        panX: view.panX + dx,
        panY: view.panY + dy,
      }));
    } else if (current.pointers.size === 2 && current.pinch) {
      const next = pinchState(current.pointers);
      const { distance, mid } = current.pinch;
      current.pinch = next;
      current.travel += DRAG_THRESHOLD;
      update((view) => {
        const zoomed = zoomAround(view, next.distance / distance, mid, origin);
        return {
          ...zoomed,
          panX: zoomed.panX + next.mid.x - mid.x,
          panY: zoomed.panY + next.mid.y - mid.y,
        };
      });
    }
  };

  const onPointerEnd = (event: ReactPointerEvent<SVGSVGElement>) => {
    const current = gesture.current;
    if (!current?.pointers.delete(event.pointerId)) return;
    current.pinch = null;
    if (current.pointers.size > 0) return;
    if (current.travel > DRAG_THRESHOLD) suppressClick.current = true;
    gesture.current = null;
    setPanning(false);
  };

  const onClickCapture = (event: ReactMouseEvent) => {
    if (!suppressClick.current) return;
    suppressClick.current = false;
    event.stopPropagation();
  };

  const onDoubleClick = (event: ReactMouseEvent<SVGSVGElement>) => {
    if ((event.target as Element).closest('[data-map-point]')) return;
    const anchor = local(event);
    update((view) => zoomAround(view, 2, anchor, origin), { immediate: true });
  };

  return {
    panning,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerEnd,
      onPointerCancel: onPointerEnd,
      onClickCapture,
      onDoubleClick,
    },
  };
};
